import { logUserActivity } from './activityLog';
import type { Enums, Json } from '@/integrations/supabase/types';

type ActivityAction =
  | Enums<'activity_action'>
  | 'facility_created'
  | 'facility_updated'
  | 'facility_deleted'
  | 'favorite_facility_added'
  | 'favorite_facility_removed';
type FacilityActivityAction =
  | 'facility_created'
  | 'facility_updated'
  | 'facility_deleted'
  | 'favorite_facility_added'
  | 'favorite_facility_removed';
type TrackedActivityAction = ActivityAction | FacilityActivityAction;
type DetailObject = Record<string, Json | null>;

const STANDARD_DETAIL_KEYS = [
  'entity_name',
  'before',
  'after',
  'reason',
  'error_code',
  'source',
] as const;

interface TrackedMutationParams<TResult> {
  action: TrackedActivityAction;
  actorUserId?: string | null;
  entityType?: string | null;
  entityId?: string | null;
  facilityId?: string | null;
  mutation: () => Promise<TResult>;
  detailsOnSuccess?: Json | ((result: TResult) => Json);
  detailsOnError?: Json | ((error: unknown) => Json);
}

const toErrorMessage = (error: unknown) => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return 'Unknown error';
};

const toErrorCode = (error: unknown): string | null => {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = (error as { code?: unknown }).code;
    return typeof code === 'string' ? code : null;
  }
  return null;
};

const resolveDetails = <T>(
  value: Json | ((input: T) => Json) | undefined,
  input: T
): Json | null => {
  if (typeof value === 'function') {
    return (value as (arg: T) => Json)(input);
  }
  return value ?? null;
};

const withOutcome = (details: Json | null, outcome: 'success' | 'failed'): Json => {
  const base =
    details && typeof details === 'object' && !Array.isArray(details)
      ? (details as DetailObject)
      : {};

  const normalizedDefaults = STANDARD_DETAIL_KEYS.reduce<DetailObject>((acc, key) => {
    if (!(key in base)) {
      acc[key] = null;
    }
    return acc;
  }, {});

  return {
    ...normalizedDefaults,
    ...base,
    outcome,
  };
};

export const executeTrackedMutation = async <TResult>({
  action,
  actorUserId,
  entityType = null,
  entityId = null,
  facilityId = null,
  mutation,
  detailsOnSuccess,
  detailsOnError,
}: TrackedMutationParams<TResult>): Promise<TResult> => {
  try {
    const result = await mutation();

    if (actorUserId) {
      const details = withOutcome(resolveDetails(detailsOnSuccess, result), 'success');
      void logUserActivity({
        userId: actorUserId,
        action,
        entityType,
        entityId,
        facilityId,
        details,
      });
    }

    return result;
  } catch (error) {
    if (actorUserId) {
      const resolvedErrorDetails = resolveDetails(detailsOnError, error);
      const errorDetails =
        resolvedErrorDetails && typeof resolvedErrorDetails === 'object' && !Array.isArray(resolvedErrorDetails)
          ? (resolvedErrorDetails as Record<string, Json>)
          : {};

      const details = withOutcome(
        {
          ...errorDetails,
          error: toErrorMessage(error),
          error_code: errorDetails.error_code ?? toErrorCode(error),
        },
        'failed'
      );

      void logUserActivity({
        userId: actorUserId,
        action,
        entityType,
        entityId,
        facilityId,
        details,
      });
    }

    throw error;
  }
};
